import { FormEvent, useState } from 'react';
import t from '../../theme';
import { toast } from 'react-toastify';
import { cancelButtonStyle, editIconStyle, saveButtonStyle } from '../../style';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    ListItemButton,
    TextField,
    Typography
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { SupportContact } from '../../lib/models/SupportModels';
import { listItemButtonStyles } from '../ListItemWideButton';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { EMPTY_GUID } from '../../lib/constants/misc';
import { formatPhoneNumber } from '../../lib/helper/format-phone-number';
import { StyledDialogActions } from '../../styledComponents';

interface ModalProps {
    formData: SupportContact;
    type: string;
    onUpsertContact: (event: FormEvent, contact: SupportContact) => void;
}

const ContactModal = (props: ModalProps) => {
    const { supportType, phoneNumber, email, isActive, id, supportInformationId } = props.formData;
    const { type, onUpsertContact } = props;

    const title = 'Contact';
    const [isDialogOpened, setDialogOpened] = useState(false);
    const [supportTypeValue, setSupportTypeValue] = useState(supportType);
    const [emailValue, setEmailValue] = useState(email);
    const [phoneNumberValue, setPhoneNumberValue] = useState(phoneNumber);
    const [phoneNumberShownValue, setPhoneNumberShownValue] = useState(
        formatPhoneNumber(phoneNumber)
    );

    const handleCloseDialog = () => setDialogOpened(false);
    const handleOpenDialog = () => setDialogOpened(true);

    const handleSave = async (event: FormEvent) => {
        event.preventDefault();
        if (phoneNumberValue.length < 10) {
            if (!(phoneNumberValue.length <= 0)) {
                toast.error('Phone number must be exactly 10 characters long');
                return;
            }
        }

        const contactUpdate: SupportContact = {
            supportType: supportTypeValue,
            phoneNumber: phoneNumberValue,
            email: emailValue,
            isActive: isActive,
            id: id,
            supportInformationId: supportInformationId
        };

        onUpsertContact(event, contactUpdate);
        if (contactUpdate.id === EMPTY_GUID) {
            setSupportTypeValue('');
            setEmailValue('');
            setPhoneNumberValue('');
        }

        //Close Dialog window
        setDialogOpened(false);
    };

    const phoneNumberChangeHandler: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        setPhoneNumberValue(e.target.value.replace(/[^\d]/g, '').substring(0, 10));
        setPhoneNumberShownValue(formatPhoneNumber(e.target.value));
    };

    return (
        <div>
            {id !== EMPTY_GUID && (
                <IconButton aria-label="edit" onClick={handleOpenDialog}>
                    <EditOutlinedIcon sx={editIconStyle} />
                </IconButton>
            )}

            {id === EMPTY_GUID && (
                <ListItemButton
                    sx={listItemButtonStyles}
                    onClick={handleOpenDialog}
                    disabled={supportInformationId === EMPTY_GUID}>
                    <Box
                        sx={{
                            display: 'flex',
                            margin: 'auto',
                            minHeight: '2.5rem',
                            alignItems: 'center'
                        }}>
                        <AddCircleOutlineIcon sx={{ margin: 'auto .375rem' }} />
                        <Typography variant="h4" sx={{ color: t.palette.primary.light }}>
                            Add {type} Support Contact Item
                        </Typography>
                    </Box>
                </ListItemButton>
            )}

            <Dialog open={isDialogOpened} onClose={handleCloseDialog}>
                <DialogTitle variant="h3">{title}</DialogTitle>
                <form onSubmit={handleSave}>
                    <DialogContent>
                        {type === 'Clinical' && (
                            <TextField
                                required
                                margin="dense"
                                label="Support Type"
                                type="text"
                                fullWidth
                                variant="outlined"
                                inputProps={{ inputMode: 'text' }}
                                value={supportTypeValue}
                                onChange={(v) => setSupportTypeValue(v.target.value)}
                            />
                        )}
                        <TextField
                            required
                            margin="dense"
                            label="Phone Number"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={phoneNumberShownValue}
                            onChange={phoneNumberChangeHandler}
                        />
                        <TextField
                            required
                            margin="dense"
                            label="Email"
                            type="email"
                            fullWidth
                            variant="outlined"
                            inputProps={{ inputMode: 'email' }}
                            value={emailValue}
                            onChange={(v) => setEmailValue(v.target.value)}
                        />
                    </DialogContent>
                    <StyledDialogActions>
                        <Button sx={cancelButtonStyle} onClick={handleCloseDialog}>
                            Cancel
                        </Button>
                        <Button sx={saveButtonStyle} type="submit">
                            Save
                        </Button>
                    </StyledDialogActions>
                </form>
            </Dialog>
        </div>
    );
};

export default ContactModal;
